/** biome-ignore-all lint/security/noDangerouslySetInnerHtml: <explanation> */
import { Module } from "@ak/game-assets"
import Modal from "@hg/modal"
import { useDraws } from "@hg/recall-frame"
import cls from "classnames"
import React, { Dispatch, FC, useEffect, useMemo, useState } from "react"

import { sfxPlay } from "@/assets/audios/sfx"
import poster from "@/assets/imgs/recall/frame-1.png"
import Button from "@/components/Button"
import { EditAddressModal } from "@/components/EditAddressModal"
import { code2Msg } from "@/components/Reward"
import RealRewardInfo from "@/components/Reward/components/RealRewardInfo"
import VirtualReward from "@/components/Reward/UI/VirtualReward"
import Toast from "@/components/Toast"
import { i18n, RecallTextPath } from "@/i18n"
import {
    type ContactInfo,
    requestPostContact,
    requestPostDraw,
} from "@/services/activity"
import { RevertRewardMap, RLRewardMap, VRRewardMap } from "@/Topics/Recall/const/reward"
import { Client } from "@/utils/gameAssets"
import { collect } from "@/utils/sdk"
import { useRoleMeta } from "@/user/session"
import { useRoleInfo } from "@/user/session"
import { triggerLogin } from "@/utils/loginTrigger"

import styles from "./index.module.scss"

// ─── Poster ──────────────────────────────────────────────────────────────────

export const Poster: FC = () => {
    const user = useRoleInfo()
    const [isDrawing, setIsDrawing] = useState(false)
    const [prizeName, setPrizeName] = useState<string | null>(null)
    const [prizeType, setPrizeType] = useState<string | null>(null)
    const [open, setOpen] = useState(false)

    const drawsSwr = useDraws()

    // 过滤出当前召回活动的可用券（status=0）
    const remainCoupons = useMemo(() => {
        if (!drawsSwr.data?.coupons) return []
        return drawsSwr.data.coupons.filter(
            (c) => c.cid === __RECALL_COUPON_CID && c.status === 0,
        )
    }, [drawsSwr.data])

    const remain = remainCoupons.length
    const disable = isDrawing || remain < 1

    const handleDraw = async () => {
        if (disable) return
        if (!user) {
            triggerLogin()
            return
        }
        const targetCid = remainCoupons[remainCoupons.length - 1].cid
        setIsDrawing(true)
        try {
            const res = await requestPostDraw(targetCid)
            if (res.code === 0 && res.data.giftName) {
                const giftName = res.data.giftName
                const pt = (res.data.prizeType as string) || ""
                setPrizeName(giftName)
                setPrizeType(pt)
                setOpen(true)
                collect("lottery", {
                    target: "recall",
                    result: pt === "GOODS" ? "material_items" : "virtual_items",
                })
            } else {
                Toast.message(code2Msg(res.code) || i18n.getRecall("message.draw_fail" as RecallTextPath) || "draw error")
            }
        } catch {
            Toast.message(i18n.getRecall("message.draw_fail" as RecallTextPath) || "draw error")
        } finally {
            setIsDrawing(false)
            drawsSwr.mutate()
        }
    }

    const contactInfo = (drawsSwr.data?.contactInfo as unknown as Record<string, ContactInfo | null> | null | undefined)?.[__RECALL_COUPON_CID] ?? null

    return (
        <>
            <div className={styles.container}>
                <div
                    className={styles.poster}
                    style={{ backgroundImage: `url(${poster})` }}
                />
                <LotteryButton
                    remain={remain}
                    disable={disable}
                    loading={isDrawing}
                    onClick={handleDraw}
                />
            </div>
            <ResultModal
                prizeName={prizeName}
                prizeType={prizeType}
                open={open}
                setOpen={setOpen}
                contactInfo={contactInfo}
                onRefresh={() => drawsSwr.mutate()}
                afterClose={() => { setPrizeName(null); setPrizeType(null) }}
            />
        </>
    )
}

// ─── Result modal ─────────────────────────────────────────────────────────────

const ResultContainer = Modal.createContainer("__RESULT_CONTAINER")
ResultContainer.rootElem.style.zIndex = "996"

const ResultModal: FC<{
    prizeName: string | null
    prizeType: string | null
    open: boolean
    setOpen: Dispatch<boolean>
    contactInfo: ContactInfo | null
    onRefresh: () => void
    afterClose: () => void
}> = ({ prizeName, prizeType, open, setOpen, contactInfo, onRefresh }) => {
    const rewardInfo = prizeName ? getRewardInfo(prizeName, prizeType) : null

    const onClose = () => setOpen(false)

    return (
        <Modal active={open} onClose={onClose} container={ResultContainer}>
            {rewardInfo && prizeType !== "GOODS" && (
                <VirtualReward
                    onClose={onClose}
                    rewardSrc={rewardInfo.src}
                    reward={rewardInfo}
                    showAmount
                />
            )}
            {rewardInfo && prizeType === "GOODS" && (
                <RealReward
                    rewardInfo={rewardInfo}
                    contactInfo={contactInfo}
                    onRefresh={onRefresh}
                    onClose={onClose}
                />
            )}
        </Modal>
    )
}

// ─── Real reward (address form) ───────────────────────────────────────────────

enum RealRewardStep {
    EDIT = "edit",
    CONFIRM = "confirm",
    DONE = "done",
    PREVIEW = "preview",
}

const RealReward = ({
    rewardInfo,
    contactInfo,
    onRefresh,
    onClose,
}: {
    rewardInfo: { src: string; title: string; total?: number | null }
    contactInfo: ContactInfo | null
    onRefresh: () => void
    onClose: () => void
}) => {
    useRoleMeta() // keep hook call order stable
    const [step, setStep] = useState<RealRewardStep>(RealRewardStep.EDIT)
    const [contact, setContact] = useState<ContactInfo>({
        name: "",
        phone: "",
        address: "",
        email: "",
        postcode: "",
        province: "",
        city: "",
        county: "",
    })
    const [loading, setLoading] = useState(false)

    // Pre-fill from server if address was already submitted
    useEffect(() => {
        if (contactInfo) {
            setStep(RealRewardStep.PREVIEW)
            setContact(contactInfo)
        }
    }, [contactInfo])

    const handleSubmitContact = async () => {
        if (!contact.name || !contact.phone || !contact.address) {
            Toast.message(i18n.getRecall("message.contact_fill_empty"))
            return
        }
        setLoading(true)
        try {
            const res = await requestPostContact(__RECALL_COUPON_CID, contact)
            if (res.code === 0) {
                setStep(RealRewardStep.DONE)
                onRefresh()
            } else {
                Toast.message(code2Msg(res.code) || "-")
            }
        } catch (error: unknown) {
            Toast.message((error as Error).message || "-")
        } finally {
            setLoading(false)
        }
    }

    return (
        <EditAddressModal
            customTitle={<RealRewardInfo reward={rewardInfo} />}
            open
            onClose={onClose}
            contactInfo={contact}
            onContactChange={setContact}
            onSubmitContact={handleSubmitContact}
            step={step}
            onStepChange={setStep}
            loading={loading}
        />
    )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getRewardInfo = (prizeName: string, prizeType?: string | null) => {
    const prizeKey = RevertRewardMap?.[prizeName]
    if (prizeType === "GOODS") {
        if (prizeKey in RLRewardMap) {
            const item = RLRewardMap[prizeKey as keyof typeof RLRewardMap]
            return {
                key: prizeKey,
                src: require("@/assets/imgs/recall/gd_card.png") as string,
                title: item.name,
                amount: item.amount,
                total: item.total,
                type: "real" as const,
            }
        }
        return null
    }
    if (prizeKey in VRRewardMap) {
        const item = VRRewardMap[prizeKey as keyof typeof VRRewardMap]
        return {
            key: prizeKey,
            src: Client.get(Module.ITEM, item.key),
            title: item.name,
            amount: item.amount,
            total: item.total,
            type: "virtual" as const,
        }
    }
    return null
}

// ─── Lottery button ───────────────────────────────────────────────────────────

const LotteryButton: FC<{
    loading?: boolean
    disable?: boolean
    remain: number
    onClick: () => void
}> = ({ loading, disable, remain, onClick }) => {
    const text = useMemo(() => i18n.getRecall("poster.button_draw"), [])
    const remainText = useMemo(
        () => i18n.getRecall("poster.text_remain", { remain }),
        [remain],
    )

    const clickHandler = () => {
        if (disable) return
        onClick()
        sfxPlay("click")
    }

    return (
        <div className={styles.lotteryBtn}>
            <Button
                text={text}
                outlinedText={loading ? "···" : text}
                className={cls(styles.btn, loading ? styles.loading : null)}
                onClick={clickHandler}
                disabled={disable}
                type="primary"
                outlineStyle={{
                    fontSize: 58,
                    fontFamily: "SourceHanSansJP-VF",
                }}
            />
            <div
                className={styles.remain}
                dangerouslySetInnerHTML={{ __html: remainText }}
            />
        </div>
    )
}
